import type { Connection, Edge, Node } from '@xyflow/react';
import { NodeTypes } from './node-types';
import { isNodeType } from './validation-helpers';

export function getFlowConnectionError(
  connection: Connection,
  nodes: Node[],
  edges: Edge[],
): string | null {
  if (connection.source === connection.target) {
    return 'Cannot connect node to itself.';
  }

  const sourceNode = nodes.find((n) => n.id === connection.source);
  const targetNode = nodes.find((n) => n.id === connection.target);
  if (!sourceNode || !targetNode) {
    return null;
  }

  const sourceType = sourceNode.type as string;
  const targetType = targetNode.type as string;
  const edgesFromSource = edges.filter((e) => e.source === connection.source);

  return (
    getSettingsConnectionError(sourceType, targetType, edgesFromSource) ??
    getTerminalInboundError(sourceType, targetType) ??
    getDebtCollectionInboundError(connection, sourceType, targetType, edges) ??
    getDebtCollectionOutgoingError(connection, sourceType, edges) ??
    getRecurringActionOutgoingError(connection, sourceType, targetType, edgesFromSource, nodes) ??
    getConditionalOutgoingError(connection, sourceType, targetType, edgesFromSource, nodes)
  );
}

function getSettingsConnectionError(
  sourceType: string,
  targetType: string,
  edgesFromSource: Edge[],
): string | null {
  if (!isNodeType(sourceType, NodeTypes.settingsNode)) {
    return null;
  }
  if (!isNodeType(targetType, NodeTypes.conditionalNode)) {
    return 'Settings Node can only connect to Conditional Node.';
  }
  if (edgesFromSource.length > 0) {
    return 'Settings Node can only connect to 1 node.';
  }
  return null;
}

function getTerminalInboundError(sourceType: string, targetType: string): string | null {
  const targetIsTerminal = isNodeType(targetType, NodeTypes.failureNode, NodeTypes.successNode);
  if (!targetIsTerminal || isNodeType(sourceType, NodeTypes.recurringActionNode)) {
    return null;
  }
  return 'Success Node and Failure Node can only receive connections from Recurring Action Node.';
}
function getDebtCollectionInboundError(
  connection: Connection,
  sourceType: string,
  targetType: string,
  edges: Edge[],
): string | null {
  if (!isNodeType(targetType, NodeTypes.debtCollectionNode)) {
    return null;
  }

  if (!isNodeType(sourceType, NodeTypes.errorNode)) {
    return 'Debt Collection Node can only receive connection from Error Node.';
  }

  const incomingToDebt = edges.filter((e) => e.target === connection.target);

  if (incomingToDebt.length >= 1) {
    const reconnectingSameEdge = incomingToDebt.some(
      (e) => e.source === connection.source && e.sourceHandle === connection.sourceHandle,
    );
    if (!reconnectingSameEdge) {
      return 'Debt Collection Node can only receive one incoming connection.';
    }
  }
  return null;
}

function getDebtCollectionOutgoingError(
  connection: Connection,
  sourceType: string,
  edges: Edge[],
): string | null {
  if (!isNodeType(sourceType, NodeTypes.debtCollectionNode)) {
    return null;
  }
  const outgoingFromDebt = edges.filter((e) => e.source === connection.source);
  if (outgoingFromDebt.length >= 1) {
    const reconnectingSameEdge = outgoingFromDebt.some(
      (e) => e.target === connection.target && e.sourceHandle === connection.sourceHandle,
    );
    if (!reconnectingSameEdge) {
      return 'Debt Collection Node can only have one outgoing connection.';
    }
  }
  return null;
}

function getRecurringActionOutgoingError(
  connection: Connection,
  sourceType: string,
  targetType: string,
  edgesFromSource: Edge[],
  nodes: Node[],
): string | null {
  if (!isNodeType(sourceType, NodeTypes.recurringActionNode)) {
    return null;
  }
  if (!isNodeType(targetType, NodeTypes.failureNode, NodeTypes.successNode)) {
    return 'Recurring Action Node can only connect to Success Node or Failure Node.';
  }

  const resolvedTargets = resolveTargetsFromEdges(edgesFromSource, nodes);

  if (isNodeType(targetType, NodeTypes.failureNode)) {
    const failureTargets = resolvedTargets.filter((n) =>
      isNodeType(n.type as string, NodeTypes.failureNode),
    );
    const reconnectingSameNode = failureTargets.some((n) => n.id === connection.target);
    if (failureTargets.length >= 1 && !reconnectingSameNode) {
      return 'Recurring Action Node can only connect to one Failure Node.';
    }
  }

  if (isNodeType(targetType, NodeTypes.successNode)) {
    const successTargets = resolvedTargets.filter((n) =>
      isNodeType(n.type as string, NodeTypes.successNode),
    );
    const reconnectingSameNode = successTargets.some((n) => n.id === connection.target);
    if (successTargets.length >= 1 && !reconnectingSameNode) {
      return 'Recurring Action Node can only connect to one Success Node.';
    }
  }

  return null;
}

function getConditionalOutgoingError(
  connection: Connection,
  sourceType: string,
  targetType: string,
  edgesFromSource: Edge[],
  nodes: Node[],
): string | null {
  if (!isNodeType(sourceType, NodeTypes.conditionalNode)) {
    return null;
  }

  const existingTargets = resolveTargetsFromEdges(edgesFromSource, nodes);

  const connectedToValue = existingTargets.some((n) =>
    isNodeType(n.type as string, NodeTypes.valueNode),
  );
  const connectedToTestGroups = existingTargets.some((n) =>
    isNodeType(n.type as string, NodeTypes.testGroupsNode),
  );
  const connectedToRecurring = existingTargets.some((n) =>
    isNodeType(n.type as string, NodeTypes.recurringActionNode),
  );
  const connectedToSubConditional = existingTargets.some((n) =>
    isNodeType(n.type as string, NodeTypes.conditionalNode),
  );

  const targetAllowed = isNodeType(
    targetType,
    NodeTypes.testGroupsNode,
    NodeTypes.valueNode,
    NodeTypes.conditionalNode,
    NodeTypes.recurringActionNode,
  );

  if (!targetAllowed) {
    return 'Conditional Node can only connect to Test Groups or Value Node.';
  }

  if (connectedToValue && isNodeType(targetType, NodeTypes.testGroupsNode)) {
    return 'Conditional Node connected to Settings Value Node cannot connect to Test Groups Node.';
  }

  if (connectedToSubConditional && isNodeType(targetType, NodeTypes.conditionalNode)) {
    return 'Conditional Node can be connected to only one Sub Conditional Node.';
  }

  if (connectedToValue && isNodeType(targetType, NodeTypes.valueNode)) {
    return 'Conditional Node can be connected to only one Settings Value Node.';
  }

  if (isNodeType(targetType, NodeTypes.recurringActionNode)) {
    const recurringTargets = existingTargets.filter((n) =>
      isNodeType(n.type as string, NodeTypes.recurringActionNode),
    );
    const sameRecurringTarget = recurringTargets.some((n) => n.id === connection.target);
    if (recurringTargets.length >= 1 && !sameRecurringTarget) {
      return 'Conditional Node can only connect to one Recurring Action Node.';
    }
  }

  if (
    (connectedToRecurring && isNodeType(targetType, NodeTypes.valueNode)) ||
    (connectedToValue && isNodeType(targetType, NodeTypes.recurringActionNode))
  ) {
    return 'Conditional Node cannot connect to both a Recurring Action Node and a Settings Value Node.';
  }

  if (
    connectedToTestGroups &&
    isNodeType(targetType, NodeTypes.valueNode, NodeTypes.recurringActionNode)
  ) {
    return `Conditional Node connected to Test Groups Node cannot connect to ${targetType} Node.`;
  }

  if (
    (connectedToRecurring || connectedToValue) &&
    isNodeType(targetType, NodeTypes.testGroupsNode)
  ) {
    const branch = connectedToRecurring ? 'Recurring Action Node' : 'Settings Value Node';
    return `Conditional Node connected to ${branch} cannot connect to Test Groups Node.`;
  }

  return null;
}

function resolveTargetsFromEdges(edgesFromSource: Edge[], nodes: Node[]): Node[] {
  return edgesFromSource
    .map((e) => nodes.find((n) => n.id === e.target))
    .filter((n): n is Node => Boolean(n));
}
